import {useLayoutEffect} from 'react'
import type {RefObject} from 'react'

// Shared size (px) for the injected resize grip, so the chart and table grips match.
export const RESIZE_GRIP_SIZE = 14

interface ResizeGripOptions {
  /**
   * 'aspect' -> ratio-locked: only the width is driven and the caller's `aspectRatio`
   * wrapper derives the height (charts). 'free' -> width and height are driven
   * independently (tables).
   */
  mode: 'aspect' | 'free'
  /** Chart area width / height (the SVG viewBox ratio). Used only in 'aspect' mode. */
  aspectRatio?: number
  /**
   * Double-clicking the grip resets to the default size. By default the inline
   * width/height are cleared (falling back to the element's CSS default); pass a callback
   * to restore a computed default instead (e.g. a table's fit-to-content size). The
   * callback must be stable (memoised) since it's an effect dependency.
   */
  onReset?: (el: HTMLElement) => void
}

/**
 * Injects a bottom-right corner grip and drives the element's resize ourselves, so the
 * chart and table grips look and behave identically (same size, same double-click reset).
 *
 * Charts resize ratio-locked: the grip follows the pointer's projection onto the growth
 * diagonal (width, width/ratio), so the motion is continuous (no dominant-axis switch to
 * jitter), the graph never distorts, and a vertical drag still grows it. Tables resize
 * freely (width and height independently). Min/max are left to the element's CSS.
 */
export function useResizeGrip(
  ref: RefObject<HTMLElement | null>,
  options: ResizeGripOptions,
): void {
  const {mode, aspectRatio = 1, onReset} = options
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return undefined

    const grip = document.createElement('div')
    grip.setAttribute('aria-hidden', 'true')
    grip.title = 'Drag to resize · double-click to reset'
    Object.assign(grip.style, {
      position: 'absolute',
      right: '2px',
      bottom: '2px',
      width: `${RESIZE_GRIP_SIZE}px`,
      height: `${RESIZE_GRIP_SIZE}px`,
      zIndex: '4',
      cursor: 'nwse-resize',
      // Two short diagonal ticks, matching the native resize affordance but theme-neutral.
      backgroundImage:
        'linear-gradient(135deg, transparent 0 45%, currentColor 45% 55%, transparent 55% 100%),' +
        'linear-gradient(135deg, transparent 0 70%, currentColor 70% 80%, transparent 80% 100%)',
      opacity: '0.35',
      touchAction: 'none',
    })
    el.appendChild(grip)

    let startX = 0
    let startY = 0
    let startWidth = 0
    let startHeight = 0
    // Timestamp of the last *tap* (a press that didn't turn into a drag), for double-tap
    // detection. -Infinity (not 0) so the first press is never mistaken for a double-tap:
    // timeStamp is ms-since-page-load, so a `0` sentinel would read any press before
    // t=300ms as one.
    let lastTapAt = -Infinity

    const onMove = (e: PointerEvent): void => {
      const dx = e.clientX - startX
      const dy = e.clientY - startY
      if (mode === 'free') {
        el.style.width = `${startWidth + dx}px`
        el.style.height = `${startHeight + dy}px`
        return
      }
      // Ratio-locked: project the pointer delta onto the growth diagonal (width, width/r).
      // Continuous in (dx, dy) -> smooth at every angle, with no empty space or distortion.
      const r = aspectRatio
      const dw = (dx * r * r + dy * r) / (r * r + 1)
      el.style.width = `${startWidth + dw}px`
    }

    const onUp = (e: PointerEvent): void => {
      grip.releasePointerCapture(e.pointerId)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      // A press that moved is a resize drag, not a tap -- it breaks any double-tap chain.
      if (Math.abs(e.clientX - startX) + Math.abs(e.clientY - startY) > 4) {
        lastTapAt = -Infinity
        return
      }
      // Two taps within 300ms = double-tap -> reset to the default size (native dblclick is
      // suppressed by preventDefault below, so we detect it ourselves).
      if (e.timeStamp - lastTapAt < 300) {
        lastTapAt = -Infinity
        if (onReset) onReset(el)
        else {
          el.style.width = ''
          el.style.height = ''
        }
      } else {
        lastTapAt = e.timeStamp
      }
    }

    const onDown = (e: PointerEvent): void => {
      e.preventDefault()
      startX = e.clientX
      startY = e.clientY
      startWidth = el.offsetWidth
      startHeight = el.offsetHeight
      grip.setPointerCapture(e.pointerId)
      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
    }

    grip.addEventListener('pointerdown', onDown)
    return () => {
      grip.removeEventListener('pointerdown', onDown)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      grip.remove()
    }
  }, [ref, mode, aspectRatio, onReset])
}
